/**
 * Safe localStorage utilities with error handling
 * Prevents application crashes from malformed JSON data
 */

/**
 * Safely parse JSON from localStorage with fallback to default value
 */
export function safeGetJSON<T>(key: string, defaultValue: T): T {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...defaultValue, ...parsed } as T;
    }
  } catch {
    // Silently clear corrupted data - avoid logging sensitive key names
    localStorage.removeItem(key);
  }
  return defaultValue;
}

/**
 * Safely get an array from localStorage
 */
export function safeGetArray<T>(key: string): T[] {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Silently clear corrupted data
    localStorage.removeItem(key);
  }
  return [];
}

/**
 * Safely get a record/object from localStorage
 */
export function safeGetRecord<T>(key: string): Record<string, T> {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Silently clear corrupted data
    localStorage.removeItem(key);
  }
  return {};
}

/**
 * Safely get a raw string from localStorage
 */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Safely set JSON to localStorage
 */
export function safeSetJSON(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // Silently fail - avoid logging sensitive key names
    return false;
  }
}

/**
 * Safely set a raw string to localStorage
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
